import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { settingsReportStyles } from "../../../styles/SettingsReport.styles";
import {
  clientNameClickableStyles,
  clientNameDisabledStyles,
  goToClientButtonStyles,
  clientNameSkeletonStyles,
  goToClientSkeletonStyles,
  pickerCardStyles,
  loadingContainerStyles,
  accountsContainerStyles,
  loadingAccountsBoxStyles,
  loadingAccountsTextStyles,
  emptyAccountsTextStyles,
  accountTooltipContainerStyles,
  selectedAccountChipStyles,
  deselectedAccountChipStyles,
} from "../../../styles/ClientPicker.styles";
import {
  useListAccountsQuery,
  useGetClientQuery,
  useListClientsQuery,
  Client,
  Account,
} from "features/trading/accounts";
import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { useReportSettings } from "./ReportSettingsContext";
import { useSettingsReportValue } from "hooks/useReportStorage";
import { PerformanceReportTypeEnum } from "helpers/types";
import {
  CHIP_HEIGHT,
  ChipSkeleton,
  FilterSkeletonRow,
} from "components/ui/FilterSkeletons";

// Constants
const PAGINATION_CONFIG = {
  page: 0,
  pageSize: 100,
} as const;

// Exported because they name WHICH client and accounts a report is for, and a
// row whose lifetime is per-account has to read the same two keys this picker
// writes -- a second spelling of either is how the two come to disagree about
// which client is open.
export const SETTINGS_CLIENT_ID_KEY = "client_id";
export const SETTINGS_ACCOUNT_IDS_KEY = "account_ids";

const parseClientId = (value: number | string): number | undefined => {
  if (typeof value !== "string" || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseAccountIds = (value: number | string): number[] => {
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is number => typeof id === "number");
  } catch {
    return [];
  }
};

// A single-select picker keeps at most the first surviving pick (a saved
// multi-account selection from before it became single-select included), and
// selects a client's only account outright since there is nothing to choose.
export const normalizeAccountSelection = (
  accountIds: number[],
  selectedIds: number[],
  singleSelect: boolean,
): { selected: number[]; deselected: number[] } => {
  let selected = selectedIds.filter((id) => accountIds.includes(id));
  if (singleSelect) {
    selected = selected.slice(0, 1);
    if (!selected.length && accountIds.length === 1) selected = [...accountIds];
  }
  const deselected = accountIds.filter((id) => !selected.includes(id));
  return { selected, deselected };
};

// Sub-components
interface AccountTooltipProps {
  account: Account;
}

const AccountTooltip = ({ account }: AccountTooltipProps) => (
  <Box sx={accountTooltipContainerStyles}>
    <Typography variant="caption" fontWeight="bold" display="block">
      Account Details
    </Typography>
    <Typography variant="caption" display="block">
      Account Number: {account.account_number}
    </Typography>
    {account.orion_account_id && (
      <Typography variant="caption" display="block">
        Orion ID: {account.orion_account_id}
      </Typography>
    )}
    {account.custodian__name && (
      <Typography variant="caption" display="block">
        Custodian: {account.custodian__name}
      </Typography>
    )}
    {account.reg_type__name && (
      <Typography variant="caption" display="block">
        Reg Type: {account.reg_type__name}
      </Typography>
    )}
    {account.as_of_date && (
      <Typography variant="caption" display="block">
        As of Date: {new Date(account.as_of_date).toLocaleDateString()}
      </Typography>
    )}
  </Box>
);

interface AccountChipProps {
  account: Account;
  isDeselected: boolean;
  canRemove: boolean;
  onRemove: (accountId: number) => void;
  onReselect: (accountId: number) => void;
}

const AccountChip = ({
  account,
  isDeselected,
  canRemove,
  onRemove,
  onReselect,
}: AccountChipProps) => (
  <Tooltip title={<AccountTooltip account={account} />} arrow placement="top">
    <Chip
      label={account.account_number}
      onDelete={
        isDeselected || !canRemove ? undefined : () => onRemove(account.id)
      }
      onClick={isDeselected ? () => onReselect(account.id) : undefined}
      size="small"
      sx={
        isDeselected ? deselectedAccountChipStyles : selectedAccountChipStyles
      }
    />
  </Tooltip>
);

interface ClientPickerCardProps {
  isOpen: boolean;
  isLoading: boolean;
  clients: Client[];
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onSelect: (client: Client | null) => void;
}

const ClientPickerCard = ({
  isOpen,
  isLoading,
  clients,
  searchTerm,
  onSearchChange,
  onSelect,
}: ClientPickerCardProps) => {
  if (!isOpen) return null;

  return (
    <Card sx={pickerCardStyles}>
      <CardContent>
        {isLoading ? (
          <Box sx={loadingContainerStyles}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Autocomplete
            options={clients}
            getOptionLabel={(option) => option.name}
            loading={isLoading}
            onInputChange={(_, newValue) => onSearchChange(newValue)}
            onChange={(_, value) => onSelect(value)}
            inputValue={searchTerm}
            open
            ListboxProps={{
              sx: {
                maxHeight: 200,
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search clients"
                variant="outlined"
                fullWidth
                placeholder="Type to search..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Typography>{option.name}</Typography>
              </Box>
            )}
            sx={{ width: "100%" }}
            noOptionsText={
              searchTerm ? "No clients found" : "Start typing to search clients"
            }
            autoFocus
          />
        )}
      </CardContent>
    </Card>
  );
};

interface AccountsListProps {
  isLoading: boolean;
  isFetching: boolean;
  accounts: Account[];
  selectedAccountIds: number[];
  deselectedAccountIds: number[];
  hasSelectedClient: boolean;
  canRemoveAccount: boolean;
  onRemoveAccount: (accountId: number) => void;
  onReselectAccount: (accountId: number) => void;
  // Chip-shaped placeholders instead of the "Loading accounts..." hint, so
  // the row is its loaded height while it loads (task 6134).
  showSkeleton?: boolean;
}

export const AccountsList = ({
  isLoading,
  isFetching,
  accounts,
  selectedAccountIds,
  deselectedAccountIds,
  hasSelectedClient,
  canRemoveAccount,
  onRemoveAccount,
  onReselectAccount,
  showSkeleton = false,
}: AccountsListProps) => {
  // Chips keep the order the API returned them in, whatever is selected:
  // grouping the selected ones first made a chip jump every time the pick
  // changed.
  const allAccounts = useMemo(
    () =>
      accounts.filter(
        (account) =>
          selectedAccountIds.includes(account.id) ||
          deselectedAccountIds.includes(account.id),
      ),
    [accounts, selectedAccountIds, deselectedAccountIds],
  );

  const isWaitingForData =
    isLoading || isFetching || (!accounts.length && hasSelectedClient);

  // Only while a request is in flight: a client with no accounts, or a
  // failed request, would otherwise keep the placeholder up for good.
  if ((isLoading || isFetching) && showSkeleton) {
    return (
      <FilterSkeletonRow label="Loading accounts" gap={0.5}>
        <ChipSkeleton width={88} />
        <ChipSkeleton width={88} />
      </FilterSkeletonRow>
    );
  }

  if (isWaitingForData && !showSkeleton) {
    return (
      <Box sx={loadingAccountsBoxStyles}>
        <CircularProgress size={16} />
        <Typography
          component="span"
          variant="body2"
          sx={loadingAccountsTextStyles}
        >
          Loading accounts...
        </Typography>
      </Box>
    );
  }

  if (allAccounts.length === 0) {
    return (
      <Typography component="span" sx={emptyAccountsTextStyles}>
        [Account Names]
      </Typography>
    );
  }

  return (
    <>
      {allAccounts.map((account) => {
        const isDeselected = deselectedAccountIds.includes(account.id);
        return (
          <AccountChip
            key={account.id}
            account={account}
            isDeselected={isDeselected}
            canRemove={canRemoveAccount}
            onRemove={onRemoveAccount}
            onReselect={onReselectAccount}
          />
        );
      })}
    </>
  );
};

// Names the icon beside the client name that opens the client's page.
export const GO_TO_CLIENT_LABEL = "Go to Client";

interface GoToClientButtonProps {
  onClick: () => void;
}

export const GoToClientButton = ({ onClick }: GoToClientButtonProps) => (
  <Tooltip title={GO_TO_CLIENT_LABEL}>
    <IconButton
      size="small"
      aria-label={GO_TO_CLIENT_LABEL}
      sx={goToClientButtonStyles}
      onClick={onClick}
    >
      <PersonIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

// Shown on the client name while it cannot be changed (task 4696).
export const CLIENT_LOCKED_HINT = "Wait for the calculation to finish";

interface ClientNameProps {
  name: string;
  disabled: boolean;
  onClick: () => void;
}

// The client name doubles as the button that opens the client search. A
// disabled one is greyed and inert, and says why on hover.
export const ClientName = ({ name, disabled, onClick }: ClientNameProps) =>
  disabled ? (
    <Tooltip title={CLIENT_LOCKED_HINT}>
      <Typography
        component="span"
        aria-disabled="true"
        sx={clientNameDisabledStyles}
      >
        {name}
      </Typography>
    </Tooltip>
  ) : (
    <Typography
      component="span"
      sx={clientNameClickableStyles}
      onClick={onClick}
    >
      {name}
    </Typography>
  );

interface ClientPickerProps {
  type: PerformanceReportTypeEnum;
  // The client cannot be changed while this is set: the Strategy Report sets
  // it while any calculation is in flight (task 4696). Accounts stay
  // switchable -- the lock is on the client only.
  disabled?: boolean;
  // When set, an icon beside a selected client's name opens that client's
  // page. It is not affected by `disabled`: navigating away changes nothing.
  onGoToClient?: () => void;
  // Told whether the client or its accounts are still loading, so the filter
  // rows that depend on them can hold their placeholders until they land.
  onLoadingChange?: (isLoading: boolean) => void;
}

const ClientPicker = ({
  type,
  disabled = false,
  onGoToClient,
  onLoadingChange,
}: ClientPickerProps) => {
  // The Strategy Report runs on exactly one account: picking one deselects the
  // rest, and the pick can be switched but never emptied.
  const singleSelect = type === PerformanceReportTypeEnum.STRATEGY;
  // The Strategy Report's filter card keeps its loaded size while it loads:
  // placeholders of each value's shape stand in for it (task 6134). The other
  // reports keep their loading hints.
  const showSkeletons = type === PerformanceReportTypeEnum.STRATEGY;
  const settingsClientIdKey = `${SETTINGS_CLIENT_ID_KEY}_${type}`;
  const settingsAccountIdsKey = `${SETTINGS_ACCOUNT_IDS_KEY}_${type}`;
  const [storedClientId, setStoredClientId] = useSettingsReportValue(
    settingsClientIdKey,
    "",
  );
  const [storedAccountIds, setStoredAccountIds] = useSettingsReportValue(
    settingsAccountIdsKey,
    "[]",
  );
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>(
    () => parseClientId(storedClientId),
  );
  const [clientSearchTerm, setClientSearchTerm] = useState<string>("");
  const [selectedAccountIds, setSelectedAccountIds] = useState<number[]>(() =>
    parseAccountIds(storedAccountIds),
  );
  const [deselectedAccountIds, setDeselectedAccountIds] = useState<number[]>(
    [],
  );
  const { updateSettings, settingsInfo } = useReportSettings();

  const { data: clientsData, isLoading: isClientsLoading } =
    useListClientsQuery({
      pagination: PAGINATION_CONFIG,
      filters: { search: clientSearchTerm.trim() },
    });

  const { data: selectedClient, isFetching: isClientFetching } =
    useGetClientQuery(selectedClientId, {
      skip: !selectedClientId,
    });

  const {
    data: accountsData,
    isLoading: isAccountsLoading,
    isFetching: isAccountsFetching,
  } = useListAccountsQuery(
    {
      pagination: PAGINATION_CONFIG,
      filters: { client: selectedClientId },
    },
    { skip: !selectedClientId },
  );

  const isClientLoading =
    !!selectedClientId && !selectedClient && isClientFetching;
  // The account this picker will settle on once its list is in. Until the
  // report settings carry it (and the client), the rows that read them are
  // still waiting, even though nothing is in flight any more.
  const expectedAccountId = useMemo(() => {
    const accountIds = (accountsData?.results ?? []).map((a) => a.id);
    return normalizeAccountSelection(
      accountIds,
      selectedAccountIds,
      singleSelect,
    ).selected[0];
  }, [accountsData?.results, selectedAccountIds, singleSelect]);
  const isPublishing =
    (!!selectedClient && settingsInfo.client_id !== selectedClient.id) ||
    (expectedAccountId !== undefined &&
      settingsInfo.account_id !== expectedAccountId);
  const isLoading =
    isClientLoading ||
    isAccountsLoading ||
    isAccountsFetching ||
    (!isAccountsFetching && isPublishing);
  // A layout effect, so the first paint already knows: a passive one would
  // let the dependent rows paint their "Select ..." hints for a frame.
  useLayoutEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);
  // Nothing is loading once this picker is gone.
  useLayoutEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const previousAccountsDataRef = useRef<Account[] | undefined>(undefined);
  const previousClientIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    setSelectedClientId(parseClientId(storedClientId));
  }, [storedClientId]);

  useEffect(() => {
    setSelectedAccountIds(parseAccountIds(storedAccountIds));
  }, [storedAccountIds]);

  useEffect(() => {
    setStoredClientId(
      selectedClientId === undefined ? "" : String(selectedClientId),
    );
  }, [selectedClientId, setStoredClientId]);

  useEffect(() => {
    setStoredAccountIds(JSON.stringify(selectedAccountIds));
  }, [selectedAccountIds, setStoredAccountIds]);

  useEffect(() => {
    const accountsChanged =
      previousAccountsDataRef.current !== accountsData?.results;
    const clientChanged = previousClientIdRef.current !== selectedClientId;

    // While a client switch is fetching, `data` still holds the PREVIOUS
    // client's accounts; normalizing against them would auto-select an account
    // of the wrong client until the new list lands.
    if (isAccountsFetching) return;

    if (accountsData?.results && accountsData.results.length > 0) {
      const accountIds = accountsData.results.map((acc) => acc.id);
      const normalized = normalizeAccountSelection(
        accountIds,
        selectedAccountIds,
        singleSelect,
      );

      // A single-select picker also re-normalizes whenever it holds more than
      // one pick, however it came to (another writer of the stored ids).
      const overSelected = singleSelect && selectedAccountIds.length > 1;

      if (accountsChanged || clientChanged || overSelected) {
        setSelectedAccountIds(normalized.selected);
        setDeselectedAccountIds(normalized.deselected);
        previousAccountsDataRef.current = accountsData.results;
        previousClientIdRef.current = selectedClientId;
      }
    } else if (!selectedClientId) {
      setSelectedAccountIds([]);
      setDeselectedAccountIds([]);
      previousAccountsDataRef.current = undefined;
      previousClientIdRef.current = undefined;
    }
  }, [
    accountsData?.results,
    isAccountsFetching,
    selectedClientId,
    selectedAccountIds,
    singleSelect,
  ]);

  // A search card left open when the lock engages would still let a client be
  // picked, so it closes with the lock.
  useEffect(() => {
    if (disabled) {
      setIsPickerOpen(false);
      setClientSearchTerm("");
    }
  }, [disabled]);

  const handleClientSelect = useCallback((client: Client | null) => {
    if (client) {
      setSelectedClientId(client.id);
      setIsPickerOpen(false);
      setClientSearchTerm("");
      setSelectedAccountIds([]);
      setDeselectedAccountIds([]);
    }
  }, []);

  const handleClientClick = useCallback(() => {
    setIsPickerOpen((prev) => {
      if (prev) setClientSearchTerm("");
      return !prev;
    });
  }, []);

  const handleClose = useCallback(() => {
    setIsPickerOpen(false);
    setClientSearchTerm("");
  }, []);

  const handleRemoveAccount = useCallback((accountId: number) => {
    setSelectedAccountIds((prev) => prev.filter((id) => id !== accountId));
    setDeselectedAccountIds((prev) => {
      if (!prev.includes(accountId)) {
        return [...prev, accountId];
      }
      return prev;
    });
  }, []);

  const handleReselectAccount = useCallback(
    (accountId: number) => {
      if (singleSelect) {
        const accountIds = (accountsData?.results ?? []).map((a) => a.id);
        setSelectedAccountIds([accountId]);
        setDeselectedAccountIds(accountIds.filter((id) => id !== accountId));
        return;
      }
      setDeselectedAccountIds((prev) => prev.filter((id) => id !== accountId));
      setSelectedAccountIds((prev) => {
        if (!prev.includes(accountId)) {
          return [...prev, accountId];
        }
        return prev;
      });
    },
    [singleSelect, accountsData?.results],
  );

  const selectedClientName = selectedClient?.name || "[Client Name]";

  const selectedAccountsAccountNumbers = useMemo(() => {
    const accounts = accountsData?.results ?? [];
    return selectedAccountIds
      .map((id) => accounts.find((a) => a.id === id)?.account_number)
      .filter(Boolean) as string[];
  }, [accountsData?.results, selectedAccountIds]);

  const selectedAccounts = useMemo(() => {
    const accounts = accountsData?.results ?? [];
    return selectedAccountIds
      .map((id) => accounts.find((a) => a.id === id))
      .filter(Boolean) as Account[];
  }, [accountsData?.results, selectedAccountIds]);

  const primaryAccountId = selectedAccounts[0]?.id ?? null;
  const primaryOrionAccountId = selectedAccounts[0]?.orion_account_id ?? null;

  useEffect(() => {
    updateSettings({
      client_name: selectedClient?.name ?? "",
      client_id: selectedClient?.id ?? null,
      account_id: primaryAccountId,
      account: selectedAccountsAccountNumbers.length
        ? selectedAccountsAccountNumbers.join(", ")
        : "",
      orion_account_id: primaryOrionAccountId,
    });
  }, [
    selectedClient?.name,
    selectedAccountsAccountNumbers,
    primaryAccountId,
    primaryOrionAccountId,
    updateSettings,
  ]);

  return (
    <Box>
      {/* Client */}
      <Box sx={settingsReportStyles.summaryItem}>
        <Typography component="span" sx={settingsReportStyles.label}>
          Client:
        </Typography>
        {showSkeletons && isClientLoading ? (
          <>
            <Skeleton
              variant="text"
              width={120}
              role="progressbar"
              aria-label="Loading client"
              sx={clientNameSkeletonStyles}
            />
            {/* The Go to Client icon lands with the name, and its button is
                taller than the text line: hold its place too. */}
            {onGoToClient && (
              <Skeleton
                variant="circular"
                aria-hidden
                width={24}
                height={24}
                sx={goToClientSkeletonStyles}
              />
            )}
          </>
        ) : (
          <ClientName
            name={selectedClientName}
            disabled={disabled}
            onClick={handleClientClick}
          />
        )}
        {onGoToClient && selectedClient && (
          <GoToClientButton onClick={onGoToClient} />
        )}
      </Box>

      {/* Client Picker Card */}
      <ClientPickerCard
        isOpen={isPickerOpen && !disabled}
        isLoading={isClientsLoading}
        clients={clientsData?.results || []}
        searchTerm={clientSearchTerm}
        onSearchChange={setClientSearchTerm}
        onSelect={handleClientSelect}
      />

      {/* Accounts */}
      <Box sx={settingsReportStyles.summaryItem}>
        <Typography component="span" sx={settingsReportStyles.label}>
          {singleSelect ? "Account:" : "Accounts:"}
        </Typography>
        <Box
          component="span"
          sx={accountsContainerStyles}
          minHeight={showSkeletons ? CHIP_HEIGHT : undefined}
        >
          <AccountsList
            isLoading={isAccountsLoading}
            isFetching={isAccountsFetching}
            accounts={accountsData?.results || []}
            selectedAccountIds={selectedAccountIds}
            deselectedAccountIds={deselectedAccountIds}
            hasSelectedClient={!!selectedClientId}
            canRemoveAccount={!singleSelect}
            onRemoveAccount={handleRemoveAccount}
            onReselectAccount={handleReselectAccount}
            showSkeleton={showSkeletons}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ClientPicker;
